import { useRef, useState } from 'react';
import { IconAlert, IconCheck, IconDownload, IconUpload } from '../../components/icons';
import { Badge, Card, EmptyState, ErrorMessage, Loading, StatCard, useAsync, useToast } from '../../components/ui';
import { api, ApiError, downloadFile } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDateTime } from '../../lib/format';

const megabyte = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * Datenbank & Datensicherung (Kap. 17).
 *
 * Vier Dinge, die zusammengehören: sehen, was da ist; sichern; zurückspielen;
 * zurücksetzen. Sie stehen hier beieinander, weil man sie in dieser
 * Reihenfolge braucht – und weil jede für sich genommen gefährlich klingt,
 * im Verbund aber eine vollständige Antwort auf die Frage gibt, was mit den
 * Daten bei einem Update passiert.
 *
 * Nur für Superadmins: Eine Sicherung geht über den gesamten Bestand aller
 * Mandanten, eine mandantenweise Trennung gäbe es hier nicht.
 */
export function StorageSettings() {
  const { user } = useAuth();
  const { notify } = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [lastResult, setLastResult] = useState<any>(null);
  const [pingResult, setPingResult] = useState<{ ok: boolean; latencyMs: number; error?: string } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const status = useAsync(
    () => (user?.isSuperadmin ? api.get('/api/storage/status') : Promise.resolve(null)),
    [user?.isSuperadmin],
  );

  if (!user?.isSuperadmin) {
    return (
      <Card>
        <EmptyState
          title="Superadmins vorbehalten"
          hint="Sicherung und Wiederherstellung umfassen den gesamten Bestand."
        />
      </Card>
    );
  }

  if (status.loading && !status.data) return <Loading />;
  const data = status.data;
  if (!data) return <ErrorMessage error={status.error} />;

  /** Lädt die Sicherung über den angemeldeten Weg – ein Link trüge keinen Token. */
  const exportBackup = async () => {
    setBusy('export');
    setError(null);
    try {
      await downloadFile(
        '/api/storage/backup',
        `facilityflow-backup-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`,
        undefined,
        true,
      );
      notify('Sicherung heruntergeladen.', 'success');
    } catch (caught) {
      if (!(caught instanceof ApiError && caught.code === 'cancelled')) setError(caught);
    } finally {
      setBusy(null);
    }
  };

  const restoreFromFile = async (file: File) => {
    const frage =
      `Sicherung „${file.name}“ einspielen?\n\n` +
      'Der gesamte aktuelle Bestand wird dabei ersetzt. Vom jetzigen Stand wird ' +
      'vorher automatisch eine Sicherung auf dem Server abgelegt.';
    if (!window.confirm(frage)) return;

    setBusy('restore');
    setError(null);
    try {
      const backup = JSON.parse(await file.text());
      const result = await api.postConfirmed('/api/storage/restore', { confirm: true, backup }, true);
      setLastResult({ art: 'restore', ...result });
      notify(`${result.rows} Zeilen in ${result.tables} Tabellen wiederhergestellt.`, 'success');
      status.reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const loadSampleData = async () => {
    if (
      !window.confirm(
        'Auf Musterdaten zurücksetzen?\n\n' +
          'Alle erfassten Daten werden ersetzt. Vom jetzigen Stand wird vorher ' +
          'automatisch eine Sicherung auf dem Server abgelegt.',
      )
    ) {
      return;
    }
    if (!window.confirm('Sind Sie sicher? Dieser Schritt betrifft den gesamten Bestand.')) return;

    setBusy('sample');
    setError(null);
    try {
      const result = await api.postConfirmed('/api/storage/sample-data', { confirm: true }, true);
      setLastResult({ art: 'sample', ...result });
      notify('Musterdaten geladen.', 'success');
      status.reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(null);
    }
  };

  const gefuellteTabellen = data.tables.filter((table: any) => table.rows > 0);

  return (
    <div className="stack">
      <ErrorMessage error={error} />

      {!data.schemaMatches && (
        <div className="alert warning">
          <IconAlert width={16} height={16} />
          <span>
            Der Schemastand der Datei ({data.schemaVersion}) weicht vom erwarteten Stand (
            {data.expectedSchemaVersion}) ab. Ein Neustart der Anwendung bringt die Datei auf den aktuellen Stand;
            vorher wird automatisch eine Kopie abgelegt.
          </span>
        </div>
      )}

      <div className="grid cols-4">
        <StatCard
          label="Datensätze"
          value={data.totalRows.toLocaleString('de-DE')}
          tone="primary"
          hint={`in ${gefuellteTabellen.length} von ${data.tables.length} Tabellen`}
        />
        <StatCard
          label={data.engine === 'postgres' ? 'Datenmenge' : 'Dateigröße'}
          value={megabyte(data.sizeBytes)}
          tone="info"
          hint={data.engine === 'postgres' ? 'PostgreSQL' : 'SQLite'}
        />
        <StatCard
          label="Schemastand"
          value={String(data.schemaVersion)}
          tone={data.schemaMatches ? 'success' : 'warning'}
          icon={data.schemaMatches ? <IconCheck width={17} height={17} /> : <IconAlert width={17} height={17} />}
          hint={data.schemaMatches ? 'passt zum Programmstand' : `erwartet: ${data.expectedSchemaVersion}`}
        />
        <StatCard
          label="Zuletzt geschrieben"
          value={data.modifiedAt ? formatDateTime(data.modifiedAt) : '–'}
          tone="accent"
          hint={`${data.backups.length} Sicherung(en) abgelegt`}
        />
      </div>

      <Card title="Ablage" subtitle="Wo die Daten liegen – maßgeblich für Volumes und Container">
        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-label">Datenbankdatei</span>
            <span className="detail-value mono small">{data.file}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Sicherungen</span>
            <span className="detail-value mono small">{data.backupDir}</span>
          </div>
        </div>
        <div className="note-box tone-info" style={{ marginTop: 12 }}>
          <IconCheck width={15} height={15} />
          <span>
            Beide Pfade lassen sich über <code>DATABASE_FILE</code> und <code>BACKUP_DIR</code> setzen. Im
            Containerbetrieb sollten sie auf ein eingehängtes Volume zeigen – dann überstehen die Daten jedes neue
            Abbild.
          </span>
        </div>
      </Card>

      <Card
        title="Verbindungstest"
        subtitle={data.engine === 'postgres' ? 'Echter Umlauf gegen den PostgreSQL-Server' : 'Echter Umlauf gegen die SQLite-Datei'}
      >
        <div className="row" style={{ gap: 10 }}>
          {pingResult && (
            <Badge variant={pingResult.ok ? 'success' : 'danger'}>
              {pingResult.ok ? `erreichbar (${pingResult.latencyMs} ms)` : 'nicht erreichbar'}
            </Badge>
          )}
          <button
            className="btn sm"
            disabled={busy !== null}
            onClick={async () => {
              setBusy('ping');
              setPingResult(null);
              try {
                const result = await api.post('/api/storage/ping');
                setPingResult(result);
                notify(
                  result.ok ? `Verbindung steht (${result.latencyMs} ms).` : `Verbindung fehlgeschlagen: ${result.error}`,
                  result.ok ? 'success' : 'error',
                );
              } catch (caught) {
                setPingResult({ ok: false, latencyMs: 0, error: caught instanceof Error ? caught.message : String(caught) });
              } finally {
                setBusy(null);
              }
            }}
          >
            {busy === 'ping' ? 'Prüfe …' : 'Verbindung testen'}
          </button>
          <span className="small subtle">
            Führt eine echte Abfrage aus, statt nur den offenen Verbindungspool zu melden.
          </span>
        </div>
        {pingResult && !pingResult.ok && pingResult.error && (
          <div className="small subtle" style={{ marginTop: 8 }}>
            {pingResult.error}
          </div>
        )}
      </Card>

      <div className="grid cols-3">
        <Card title="Backup exportieren" subtitle="Vollständige Sicherung als JSON-Datei">
          <p className="small subtle">
            Enthält alle Tabellen samt Anhängen. Die Datei ist lesbar und lässt sich auch außerhalb dieser Anwendung
            auswerten.
          </p>
          <button className="btn primary" onClick={exportBackup} disabled={busy !== null}>
            <IconDownload width={14} height={14} /> {busy === 'export' ? 'Wird erstellt …' : 'Herunterladen'}
          </button>
          <button
            className="btn sm"
            style={{ marginTop: 8 }}
            disabled={busy !== null}
            onClick={async () => {
              setBusy('server');
              try {
                const result = await api.postConfirmed('/api/storage/backup', undefined, true);
                notify(`Auf dem Server abgelegt: ${result.file}`, 'success');
                status.reload();
              } catch (caught) {
                setError(caught);
              } finally {
                setBusy(null);
              }
            }}
          >
            Zusätzlich auf dem Server ablegen
          </button>
        </Card>

        <Card title="Backup wiederherstellen" subtitle="Zuvor gesicherte JSON-Datei einspielen">
          <p className="small subtle">
            Ersetzt den gesamten Bestand. Vom jetzigen Stand wird vorher automatisch eine Sicherung abgelegt. Felder,
            die es heute nicht mehr gibt, werden übergangen; neue Felder behalten ihren Vorgabewert.
          </p>
          <label className="btn" style={{ cursor: busy ? 'default' : 'pointer' }}>
            <IconUpload width={14} height={14} /> {busy === 'restore' ? 'Wird eingespielt …' : 'Datei wählen …'}
            <input
              ref={fileInput}
              type="file"
              accept="application/json,.json"
              style={{ display: 'none' }}
              disabled={busy !== null}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void restoreFromFile(file);
              }}
            />
          </label>
        </Card>

        <Card title="Musterdaten laden" subtitle="Kontrolliertes Zurücksetzen auf den Demonstrationsstand">
          <p className="small subtle">
            Ersetzt alle erfassten Daten durch die mitgelieferten Beispieldaten. Zwei Rückfragen und eine Sicherung
            stehen davor – gedacht für Schulung und Erprobung, nicht für den Betrieb.
          </p>
          {data.sampleResetAvailable ? (
            <button className="btn danger" onClick={loadSampleData} disabled={busy !== null}>
              {busy === 'sample' ? 'Wird geladen …' : 'Musterdaten laden'}
            </button>
          ) : (
            <p className="small subtle">
              Im Produktivbetrieb gesperrt: Der Seed legt bekannte Demo-Zugänge an und leert vorher alle
              Tabellen. Zum bewussten Freischalten <code>ALLOW_SEED_IN_PRODUCTION=1</code> setzen.
            </p>
          )}
        </Card>
      </div>

      {lastResult?.art === 'restore' && (
        <Card title="Ergebnis der Wiederherstellung">
          <div className="stack" style={{ gap: 6 }}>
            <span>
              <strong>{lastResult.rows}</strong> Zeilen in <strong>{lastResult.tables}</strong> Tabellen eingespielt.
            </span>
            <span className="small subtle">
              Sicherung des vorherigen Stands: <span className="mono">{lastResult.safetyBackup}</span>
            </span>
            {lastResult.skippedTables?.length > 0 && (
              <span className="small subtle">
                Übergangene Tabellen (in dieser Version nicht mehr vorhanden):{' '}
                {lastResult.skippedTables.join(', ')}
              </span>
            )}
            {lastResult.droppedColumns?.length > 0 && (
              <span className="small subtle">
                Übergangene Felder:{' '}
                {lastResult.droppedColumns
                  .map((entry: any) => `${entry.table} (${entry.columns.join(', ')})`)
                  .join(' · ')}
              </span>
            )}
          </div>
        </Card>
      )}

      <div className="grid cols-2">
        <Card title="Abgelegte Sicherungen" subtitle="Auf dem Server, neueste zuerst" noBody>
          {data.backups.length === 0 ? (
            <div className="card-body">
              <span className="small subtle">Noch keine Sicherung abgelegt.</span>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Datei</th>
                    <th className="num">Größe</th>
                    <th>Erstellt</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.backups.map((backup: any) => (
                    <tr key={backup.name}>
                      <td className="mono small">{backup.name}</td>
                      <td className="num small">{megabyte(backup.sizeBytes)}</td>
                      <td className="small subtle">{formatDateTime(backup.createdAt)}</td>
                      <td className="actions">
                        {backup.name.endsWith('.json') && (
                          <button
                            className="btn sm ghost"
                            title="Herunterladen"
                            onClick={async () => {
                              await downloadFile(`/api/storage/backup/${encodeURIComponent(backup.name)}`, backup.name, undefined, true);
                            }}
                          >
                            <IconDownload width={13} height={13} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Bestand je Tabelle" subtitle={`${gefuellteTabellen.length} Tabellen mit Inhalt`} noBody>
          <div className="table-wrap" style={{ maxHeight: 340, overflowY: 'auto' }}>
            <table className="data">
              <thead>
                <tr>
                  <th>Tabelle</th>
                  <th className="num">Datensätze</th>
                </tr>
              </thead>
              <tbody>
                {gefuellteTabellen
                  .slice()
                  .sort((a: any, b: any) => b.rows - a.rows)
                  .map((table: any) => (
                    <tr key={table.name}>
                      <td className="mono small">{table.name}</td>
                      <td className="num">{table.rows.toLocaleString('de-DE')}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Card
        title="Integrität"
        subtitle={
          data.engine === 'postgres'
            ? 'PostgreSQL prüft seinen Bestand selbst – eine Prüfung auf Zuruf gibt es dort nicht'
            : 'SQLites eigene Prüfung des Dateiaufbaus'
        }
      >
        <div className="row" style={{ gap: 10 }}>
          <Badge
            variant={data.integrity === 'ok' ? 'success' : data.integrity === 'fehlerhaft' ? 'danger' : ''}
          >
            {data.integrity === 'ok' ? 'in Ordnung' : data.integrity === 'fehlerhaft' ? 'fehlerhaft' : 'ungeprüft'}
          </Badge>
          <button
            className="btn sm"
            disabled={busy !== null || data.engine === 'postgres'}
            onClick={async () => {
              setBusy('integrity');
              try {
                const result = await api.get('/api/storage/status', { integrity: 'true' });
                status.reload();
                notify(
                  result.integrity === 'ok' ? 'Die Datei ist in Ordnung.' : 'Die Prüfung meldet Fehler.',
                  result.integrity === 'ok' ? 'success' : 'error',
                );
              } finally {
                setBusy(null);
              }
            }}
          >
            {busy === 'integrity' ? 'Prüfe …' : 'Jetzt prüfen'}
          </button>
          <span className="small subtle">
            {data.engine === 'postgres'
              ? 'PostgreSQL hält seinen Bestand selbst instand.'
              : 'Kostet bei großen Beständen etwas Zeit – deshalb nicht bei jedem Aufruf.'}
          </span>
        </div>
      </Card>
    </div>
  );
}
